import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useState } from "react";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

export default function SliderComponent({
  title,
  onChange,
  value,
  minDistance = 50,
  min = 0,
  max = 3000,
  ...props
}: any) {
  const [value1, setValue1] = useState<any>([min, max]);

  useEffect(() => {
    const isNull =
      !Array.isArray(value) || value[0] === null || value[1] === null;
    setValue1(isNull ? [min, max] : value);
  }, [value, min, max]);

  const handleChange = useCallback(
    (event: Event, newValue: number | number[], activeThumb: number) => {
      if (!Array.isArray(newValue)) {
        setValue1(newValue);
        return;
      }

      if (newValue[1] - newValue[0] < minDistance) {
        if (activeThumb === 0) {
          const clamped = Math.min(newValue[0], max - minDistance);
          setValue1([clamped, clamped + minDistance]);
        } else {
          const clamped = Math.max(newValue[1], minDistance);
          setValue1([clamped - minDistance, clamped]);
        }
      } else {
        setValue1(newValue as number[]);
      }
    },
    [minDistance, max]
  );

  const handleCommit = useCallback(() => {
    // When the slider is at full range, reset to [null, null] = no filter
    const isFullRange =
      Array.isArray(value1) && value1[0] <= min && value1[1] >= max;
    onChange(isFullRange ? [null, null] : value1);
  }, [value1, min, max, onChange]);

  return (
    <div>
      <Typography
        gutterBottom
        sx={{ ...typography.body.l, color: semanticColors.text.primary }}
      >
        {title}
      </Typography>

      <Slider
        valueLabelDisplay="auto"
        disableSwap
        value={value1}
        min={min}
        max={max}
        onChange={handleChange}
        onChangeCommitted={handleCommit}
        sx={{
          padding: "9px 0!important",
          height: 8,
          color: semanticColors.highlight.default,
          "& .MuiSlider-rail": {
            backgroundColor: semanticColors.highlight.subtle,
            opacity: 1
          },
          "& .MuiSlider-track": {
            border: "none",
            background: semanticColors.gradient.surface
          },
          "& .MuiSlider-thumb": {
            height: 24,
            width: 24,
            backgroundColor: semanticColors.surface.canvas,
            border: `2px solid ${semanticColors.highlight.default}`,
            "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
              boxShadow: "inherit"
            },
            "&:before": {
              display: "none"
            },
            "&:last-of-type": {
              borderColor: semanticColors.highlight.strong
            }
          },
          "& .MuiSlider-valueLabel": {
            lineHeight: 1.2,
            fontSize: 12,
            background: "unset",
            padding: 0,
            width: 32,
            height: 32,
            borderRadius: "50% 50% 50% 0",
            backgroundColor: semanticColors.action.primary,
            transformOrigin: "bottom left",
            transform: "translate(50%, -100%) rotate(-45deg) scale(0)",
            "&:before": { display: "none" },
            "&.MuiSlider-valueLabelOpen": {
              transform: "translate(50%, -100%) rotate(-45deg) scale(1)"
            },
            "& > *": {
              transform: "rotate(45deg)"
            }
          }
        }}
        {...props}
      />
    </div>
  );
}
